import { useState } from "react";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Plus, Trash2, Loader2 } from "lucide-react";
import { useCategories } from "@/context/CategoryContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ELEVATED = ["super admin", "admin", "supervision"];

/**
 * Category picker with inline add / delete. Adding and deleting is restricted to
 * Supervision & Super admin; everyone else can only pick from the shared list.
 * The list is workspace-wide, so changes reflect across every ledger.
 */
export function CategoryField({ catType, value, onChange, testid }) {
  const { cats, addCategory, removeCategory } = useCategories();
  const { user } = useAuth();
  const canManage = ELEVATED.includes(String(user?.role || "").toLowerCase());
  const options = cats[catType] || [];
  const [open, setOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const name = newCat.trim();
    if (!name) return;
    setBusy(true);
    try {
      await addCategory(catType, name);
      onChange(name);
      setNewCat("");
      toast.success(`Added "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add category");
    } finally {
      setBusy(false);
    }
  };

  const del = async (name) => {
    setBusy(true);
    try {
      await removeCategory(catType, name);
      if (value === name) onChange("");
      toast.success(`Removed "${name}" from the pick-list`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove category");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          type="button"
          className="w-full justify-between font-normal"
          data-testid={testid}
        >
          <span className={value ? "" : "text-muted-foreground"}>{value || "Select category"}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <div className="max-h-56 overflow-y-auto py-1">
          {options.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">No categories yet</p>
          )}
          {options.map((o) => (
            <div
              key={o}
              className="group flex items-center justify-between px-2 py-1.5 hover:bg-foreground/[0.05]"
            >
              <button
                type="button"
                onClick={() => {
                  onChange(o);
                  setOpen(false);
                }}
                className="flex items-center gap-2 flex-1 text-left text-sm"
                data-testid={`${testid}-opt-${o}`}
              >
                <Check
                  className={`h-3.5 w-3.5 shrink-0 ${value === o ? "opacity-100 text-brand" : "opacity-0"}`}
                />
                <span className="truncate">{o}</span>
              </button>
              {canManage && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => del(o)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-danger p-1 transition-opacity"
                  title={`Delete "${o}"`}
                  data-testid={`${testid}-del-${o}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        {canManage && (
          <div className="border-t border-border p-2 flex items-center gap-2">
            <Input
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="New category"
              className="h-8 text-sm"
              data-testid={`${testid}-new-input`}
            />
            <Button
              size="sm"
              type="button"
              className="h-8 px-2 shrink-0"
              onClick={add}
              disabled={busy || !newCat.trim()}
              data-testid={`${testid}-add-btn`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
